import { Block, BlockType, SummaryRow, NoteRow } from "../blockModel/types";
import { serialize } from "../blockModel/serialize";
import { BuilderState, ElStyle, Theme, nid } from "./state";

// ---------------------------------------------------------------------------
// newBlock factory
// ---------------------------------------------------------------------------

export function newBlock(type: BlockType): Block {
	const id = nid();
	switch (type) {
		case "paragraph":
			return { id, type: "paragraph", data: { html: "Nội dung đoạn văn." } };
		case "summary":
			return {
				id,
				type: "summary",
				required: false,
				data: { rows: [{ label: "Nhãn", value: "${value}", emphasis: false }] },
			};
		case "notes":
			return {
				id,
				type: "notes",
				style: { color: "neutral", layout: "list" },
				data: { title: "Ghi chú", items: ["Dòng ghi chú mới"] },
			};
		case "button":
			return { id, type: "button", data: { label: "Xem chi tiết" } };
		case "chip":
			return { id, type: "chip", data: { text: "Trạng thái" } };
		case "divider":
			return { id, type: "divider", data: {} };
		default:
			return { id, type, data: {} };
	}
}

// ---------------------------------------------------------------------------
// footerIndex — index of first footer block, else blocks.length
// ---------------------------------------------------------------------------

export function footerIndex(blocks: Block[]): number {
	const idx = blocks.findIndex((b) => b.type === "footer");
	return idx === -1 ? blocks.length : idx;
}

// ---------------------------------------------------------------------------
// addBlock — insert newBlock(type) before footer; select it
// ---------------------------------------------------------------------------

export function addBlock(state: BuilderState, type: BlockType): BuilderState {
	const block = newBlock(type);
	const insertAt = footerIndex(state.blocks);
	const blocks = [...state.blocks.slice(0, insertAt), block, ...state.blocks.slice(insertAt)];
	return { ...state, blocks, selId: block.id!, selEl: null };
}

// ---------------------------------------------------------------------------
// addBlockAt — insert newBlock(type) before `beforeId` (null → before footer);
// used by palette drag-drop. Header stays first, footer stays last: a drop
// targeting the header inserts AFTER it instead of above.
// ---------------------------------------------------------------------------

export function addBlockAt(state: BuilderState, type: BlockType, beforeId: string | null): BuilderState {
	const block = newBlock(type);
	let insertAt: number;
	if (beforeId === null) {
		insertAt = footerIndex(state.blocks);
	} else {
		const idx = state.blocks.findIndex((b) => b.id === beforeId);
		insertAt = idx === -1 ? footerIndex(state.blocks) : idx;
		// Never insert above a leading header block.
		if (insertAt === 0 && state.blocks[0]?.type === "header") insertAt = 1;
	}
	const blocks = [...state.blocks.slice(0, insertAt), block, ...state.blocks.slice(insertAt)];
	return { ...state, blocks, selId: block.id!, selEl: null };
}

// ---------------------------------------------------------------------------
// delBlock — remove unless required; clear sel if pointed at it
// ---------------------------------------------------------------------------

export function delBlock(state: BuilderState, id: string): BuilderState {
	const target = state.blocks.find((b) => b.id === id);
	// Only the structural header/footer are protected — they can't be re-added from
	// the palette. Everything else (incl. summary marked required) is deletable.
	if (!target || target.type === "header" || target.type === "footer") return state;
	const blocks = state.blocks.filter((b) => b.id !== id);
	const selId = state.selId === id ? null : state.selId;
	const selEl = state.selId === id ? null : state.selEl;
	return { ...state, blocks, selId, selEl };
}

// ---------------------------------------------------------------------------
// moveBlock — reorder so fromId sits before beforeId (null → append)
// ---------------------------------------------------------------------------

export function moveBlock(state: BuilderState, fromId: string, beforeId: string | null): BuilderState {
	const moving = state.blocks.find((b) => b.id === fromId);
	if (!moving) return state;
	const without = state.blocks.filter((b) => b.id !== fromId);
	if (beforeId === null) {
		return { ...state, blocks: [...without, moving] };
	}
	const insertAt = without.findIndex((b) => b.id === beforeId);
	if (insertAt === -1) return { ...state, blocks: [...without, moving] };
	const blocks = [...without.slice(0, insertAt), moving, ...without.slice(insertAt)];
	return { ...state, blocks };
}

// ---------------------------------------------------------------------------
// dupBlock — clone block, new id, required=false, copy elStyle, insert after
// ---------------------------------------------------------------------------

export function dupBlock(state: BuilderState, id: string): BuilderState {
	const origIdx = state.blocks.findIndex((b) => b.id === id);
	if (origIdx === -1) return state;
	const original = state.blocks[origIdx];
	const clone: Block = { ...JSON.parse(JSON.stringify(original)), id: nid(), required: false };

	// Copy elStyle keys from old prefix to new prefix
	const prefix = id + ":";
	const newPrefix = clone.id! + ":";
	const elStyle: Record<string, ElStyle> = { ...state.elStyle };
	Object.keys(state.elStyle).forEach((key) => {
		if (key.startsWith(prefix)) {
			elStyle[newPrefix + key.slice(prefix.length)] = { ...state.elStyle[key] };
		}
	});

	const blocks = [...state.blocks.slice(0, origIdx + 1), clone, ...state.blocks.slice(origIdx + 1)];
	return { ...state, blocks, elStyle, selId: clone.id!, selEl: null };
}

// ---------------------------------------------------------------------------
// addRow — push a new row/item/line depending on block type
// ---------------------------------------------------------------------------

export function addRow(state: BuilderState, blockId: string): BuilderState {
	return updateBlock(state, blockId, (b) => {
		switch (b.type) {
			case "summary": {
				const rows = [
					...((b.data.rows as SummaryRow[]) || []),
					{ label: "Nhãn mới", value: "${value}", emphasis: false },
				];
				return { ...b, data: { ...b.data, rows } };
			}
			case "notes":
				if (b.style?.layout === "rows") {
					const rows = [...((b.data.rows as NoteRow[]) || []), { k: "Nhãn mới:", v: "Giá trị" }];
					return { ...b, data: { ...b.data, rows } };
				} else {
					const items = [...(b.data.items || []), "Dòng ghi chú mới"];
					return { ...b, data: { ...b.data, items } };
				}
			case "footer": {
				const lines = [...(b.data.lines || []), "Nhãn: nội dung"];
				return { ...b, data: { ...b.data, lines } };
			}
			default:
				return b;
		}
	});
}

// ---------------------------------------------------------------------------
// delRow — remove row/item/line at index (summary keeps >=1)
// ---------------------------------------------------------------------------

export function delRow(state: BuilderState, blockId: string, index: number): BuilderState {
	return updateBlock(state, blockId, (b) => {
		switch (b.type) {
			case "summary": {
				const rows = (b.data.rows as SummaryRow[]) || [];
				if (rows.length <= 1) return b;
				return { ...b, data: { ...b.data, rows: rows.filter((_, i) => i !== index) } };
			}
			case "notes":
				if (b.style?.layout === "rows") {
					const rows = ((b.data.rows as NoteRow[]) || []).filter((_, i) => i !== index);
					return { ...b, data: { ...b.data, rows } };
				} else {
					const items = (b.data.items || []).filter((_, i) => i !== index);
					return { ...b, data: { ...b.data, items } };
				}
			case "footer": {
				const lines = (b.data.lines || []).filter((_, i) => i !== index);
				return { ...b, data: { ...b.data, lines } };
			}
			default:
				return b;
		}
	});
}

// ---------------------------------------------------------------------------
// dupRow — clone row/item/line after its current index
// ---------------------------------------------------------------------------

export function dupRow(state: BuilderState, blockId: string, index: number): BuilderState {
	return updateBlock(state, blockId, (b) => {
		switch (b.type) {
			case "summary": {
				const rows = (b.data.rows as SummaryRow[]) || [];
				const clone = JSON.parse(JSON.stringify(rows[index]));
				const next = [...rows.slice(0, index + 1), clone, ...rows.slice(index + 1)];
				return { ...b, data: { ...b.data, rows: next } };
			}
			case "notes":
				if (b.style?.layout === "rows") {
					const rows = (b.data.rows as NoteRow[]) || [];
					const clone = { ...rows[index] };
					const next = [...rows.slice(0, index + 1), clone, ...rows.slice(index + 1)];
					return { ...b, data: { ...b.data, rows: next } };
				} else {
					const items = b.data.items || [];
					const next = [...items.slice(0, index + 1), items[index], ...items.slice(index + 1)];
					return { ...b, data: { ...b.data, items: next } };
				}
			case "footer": {
				const lines = b.data.lines || [];
				const next = [...lines.slice(0, index + 1), lines[index], ...lines.slice(index + 1)];
				return { ...b, data: { ...b.data, lines: next } };
			}
			default:
				return b;
		}
	});
}

// ---------------------------------------------------------------------------
// moveRow — reorder row/item/line from one index to another (same block)
// ---------------------------------------------------------------------------

function arrMove<T>(arr: T[], from: number, to: number): T[] {
	if (from === to || from < 0 || from >= arr.length) return arr;
	const next = [...arr];
	const [item] = next.splice(from, 1);
	next.splice(Math.max(0, Math.min(to, next.length)), 0, item);
	return next;
}

/** Element-style fields that are indexed per row. */
const ROW_STYLE_FIELDS = ["rlbl", "rval", "rlbl2", "rval2", "nk", "nv", "nli", "fline"];

/** Remap per-row elStyle keys (`blockId:field:idx`) so styles follow their row after a move. */
function remapRowElStyle(
	elStyle: Record<string, ElStyle>,
	blockId: string,
	from: number,
	to: number,
	length: number
): Record<string, ElStyle> {
	const mapIndex = (i: number): number => {
		if (i === from) return to;
		if (from < to && i > from && i <= to) return i - 1;
		if (from > to && i >= to && i < from) return i + 1;
		return i;
	};
	const next: Record<string, ElStyle> = {};
	Object.keys(elStyle).forEach((key) => {
		const parts = key.split(":");
		if (parts[0] === blockId && parts.length === 3 && ROW_STYLE_FIELDS.includes(parts[1])) {
			const idx = parseInt(parts[2], 10);
			if (!isNaN(idx) && idx < length) {
				next[`${parts[0]}:${parts[1]}:${mapIndex(idx)}`] = elStyle[key];
				return;
			}
		}
		next[key] = elStyle[key];
	});
	return next;
}

export function moveRow(state: BuilderState, blockId: string, from: number, to: number): BuilderState {
	if (from === to) return state;
	let rowCount = 0;
	const moved = updateBlock(state, blockId, (b) => {
		switch (b.type) {
			case "header": {
				const lines = b.data.lines || [];
				rowCount = lines.length;
				return { ...b, data: { ...b.data, lines: arrMove(lines, from, to) } };
			}
			case "summary": {
				const rows = (b.data.rows as SummaryRow[]) || [];
				rowCount = rows.length;
				return { ...b, data: { ...b.data, rows: arrMove(rows, from, to) } };
			}
			case "notes":
				if (b.style?.layout === "rows") {
					const rows = (b.data.rows as NoteRow[]) || [];
					rowCount = rows.length;
					return { ...b, data: { ...b.data, rows: arrMove(rows, from, to) } };
				} else {
					const items = b.data.items || [];
					rowCount = items.length;
					return { ...b, data: { ...b.data, items: arrMove(items, from, to) } };
				}
			case "footer": {
				const lines = b.data.lines || [];
				rowCount = lines.length;
				return { ...b, data: { ...b.data, lines: arrMove(lines, from, to) } };
			}
			default:
				return b;
		}
	});
	if (moved === state || rowCount === 0 || from < 0 || from >= rowCount) return moved;
	return {
		...moved,
		elStyle: remapRowElStyle(moved.elStyle, blockId, from, Math.max(0, Math.min(to, rowCount - 1)), rowCount),
	};
}

// ---------------------------------------------------------------------------
// setBlockStyle — merge patch into block.style
// ---------------------------------------------------------------------------

export function setBlockStyle(state: BuilderState, blockId: string, patch: Block["style"]): BuilderState {
	return updateBlock(state, blockId, (b) => ({
		...b,
		style: { ...b.style, ...patch },
	}));
}

// ---------------------------------------------------------------------------
// setElStyle — merge patch into elStyle[elKey]
// ---------------------------------------------------------------------------

export function setElStyle(state: BuilderState, elKey: string, patch: ElStyle): BuilderState {
	return {
		...state,
		elStyle: {
			...state.elStyle,
			[elKey]: { ...state.elStyle[elKey], ...patch },
		},
	};
}

// ---------------------------------------------------------------------------
// setTheme — merge patch into theme
// ---------------------------------------------------------------------------

export function setTheme(state: BuilderState, patch: Partial<Theme>): BuilderState {
	return { ...state, theme: { ...state.theme, ...patch } };
}

// ---------------------------------------------------------------------------
// setBlockData — merge patch into block.data
// ---------------------------------------------------------------------------

export function setBlockData(state: BuilderState, blockId: string, patch: Partial<Block["data"]>): BuilderState {
	return updateBlock(state, blockId, (b) => ({
		...b,
		data: { ...b.data, ...patch },
	}));
}

// ---------------------------------------------------------------------------
// toContent — serialize blocks with ids stripped
// ---------------------------------------------------------------------------

export function toContent(state: BuilderState): string {
	return serialize(state.blocks.map(({ id, ...b }) => b));
}

// ---------------------------------------------------------------------------
// missingRequiredVars — collect requiredVars not found in block.data JSON
// ---------------------------------------------------------------------------

export function missingRequiredVars(state: BuilderState): string[] {
	const missing: string[] = [];
	for (const block of state.blocks) {
		if (!block.requiredVars || block.requiredVars.length === 0) continue;
		const dataJson = JSON.stringify(block.data);
		for (const v of block.requiredVars) {
			if (!dataJson.includes(v)) {
				missing.push(v);
			}
		}
	}
	return missing;
}

// ---------------------------------------------------------------------------
// setRowInline — toggle the inline layout flag on a specific summary row.
// ---------------------------------------------------------------------------

export function setRowInline(state: BuilderState, blockId: string, index: number, value: boolean): BuilderState {
	return updateBlock(state, blockId, (b) => {
		const rows = [...((b.data.rows as SummaryRow[]) || [])];
		if (!rows[index]) return b;
		rows[index] = { ...rows[index], inline: value };
		return { ...b, data: { ...b.data, rows } };
	});
}

// ---------------------------------------------------------------------------
// mergeRow — merge summary row at `index` with the next row into one pair row.
// Only valid when both row[index] and row[index+1] are single (non-pair, non-raw) rows.
// ---------------------------------------------------------------------------

export function mergeRow(state: BuilderState, blockId: string, index: number): BuilderState {
	return updateBlock(state, blockId, (b) => {
		if (b.type !== "summary") return b;
		const rows = [...((b.data.rows as SummaryRow[]) || [])];
		const a = rows[index];
		const bRow = rows[index + 1];
		// Guard: both must exist and be plain single rows (no raw, no existing label2)
		if (!a || !bRow) return b;
		if (a.raw !== undefined || bRow.raw !== undefined) return b;
		if (a.label2 !== undefined || bRow.label2 !== undefined) return b;
		const merged: SummaryRow = {
			label: a.label,
			value: a.value,
			emphasis: a.emphasis,
			label2: bRow.label ?? "",
			value2: bRow.value ?? "",
			emphasis2: bRow.emphasis,
		};
		const next = [...rows.slice(0, index), merged, ...rows.slice(index + 2)];
		return { ...b, data: { ...b.data, rows: next } };
	});
}

// ---------------------------------------------------------------------------
// splitRow — split a pair row at `index` back into two single rows.
// Only valid when row[index] has label2 set (is a pair row).
// ---------------------------------------------------------------------------

export function splitRow(state: BuilderState, blockId: string, index: number): BuilderState {
	return updateBlock(state, blockId, (b) => {
		if (b.type !== "summary") return b;
		const rows = [...((b.data.rows as SummaryRow[]) || [])];
		const row = rows[index];
		if (!row || row.label2 === undefined) return b;
		const left: SummaryRow = { label: row.label, value: row.value, emphasis: row.emphasis };
		const right: SummaryRow = { label: row.label2, value: row.value2, emphasis: row.emphasis2 };
		const next = [...rows.slice(0, index), left, right, ...rows.slice(index + 1)];
		return { ...b, data: { ...b.data, rows: next } };
	});
}

// ---------------------------------------------------------------------------
// addHdrLine — append an empty line to header.data.lines
// ---------------------------------------------------------------------------

export function addHdrLine(state: BuilderState, blockId: string): BuilderState {
	return updateBlock(state, blockId, (b) => {
		if (b.type !== "header") return b;
		const lines = [...(b.data.lines || []), ""];
		return { ...b, data: { ...b.data, lines } };
	});
}

// ---------------------------------------------------------------------------
// delHdrLine — remove header line at index
// ---------------------------------------------------------------------------

export function delHdrLine(state: BuilderState, blockId: string, index: number): BuilderState {
	return updateBlock(state, blockId, (b) => {
		if (b.type !== "header") return b;
		const lines = (b.data.lines || []).filter((_, i) => i !== index);
		return { ...b, data: { ...b.data, lines: lines.length ? lines : undefined } };
	});
}

// ---------------------------------------------------------------------------
// isSkeletonEmail — true when there is no summary block in the state.
// Used to decide whether a preset should build the full layout or just restyle.
// ---------------------------------------------------------------------------

export function isSkeletonEmail(state: BuilderState): boolean {
	return !state.blocks.some((b) => b.type === "summary");
}

// ---------------------------------------------------------------------------
// addSectionHeader — append a raw section-header row to a summary block.
// ---------------------------------------------------------------------------

export function addSectionHeader(state: BuilderState, blockId: string): BuilderState {
	return updateBlock(state, blockId, (b) => {
		if (b.type !== "summary") return b;
		const rows = [...((b.data.rows as SummaryRow[]) || []), { raw: "# New section" }];
		return { ...b, data: { ...b.data, rows } };
	});
}

// ---------------------------------------------------------------------------
// addSectionAt — insert a raw section-header row into a summary at `index`.
// Used by palette drag-drop so a "Tiêu đề mục" can be dropped between rows.
// index is clamped to [0, rows.length].
// ---------------------------------------------------------------------------

export function addSectionAt(state: BuilderState, blockId: string, index: number): BuilderState {
	return updateBlock(state, blockId, (b) => {
		if (b.type !== "summary") return b;
		const rows = [...((b.data.rows as SummaryRow[]) || [])];
		const at = Math.max(0, Math.min(index, rows.length));
		rows.splice(at, 0, { raw: "# New section" });
		return { ...b, data: { ...b.data, rows } };
	});
}

// ---------------------------------------------------------------------------
// toggleRowEmphasis — flip emphasis on the left or right side of a summary row.
// ---------------------------------------------------------------------------

export function toggleRowEmphasis(
	state: BuilderState,
	blockId: string,
	index: number,
	side: "left" | "right"
): BuilderState {
	return updateBlock(state, blockId, (b) => {
		if (b.type !== "summary") return b;
		const rows = [...((b.data.rows as SummaryRow[]) || [])];
		if (!rows[index]) return b;
		if (side === "left") {
			rows[index] = { ...rows[index], emphasis: !rows[index].emphasis };
		} else {
			rows[index] = { ...rows[index], emphasis2: !rows[index].emphasis2 };
		}
		return { ...b, data: { ...b.data, rows } };
	});
}

// ---------------------------------------------------------------------------
// setRowText — update a single text field inside a row/item/line by index.
//
// Supported field values:
//   summary:  "rlbl" (row.label) | "rval" (row.value) | "rraw" (row.raw title)
//   notes:    "nk" (row.k) | "nv" (row.v) | "nli" (items[index])
//   footer:   "fline" (lines[index])
//   header:   "hdrline" (lines[index])
//   direct block.data fields: "title" | "subtitle" | "html" | "text" | "label" | "note" | "ntitle"
// ---------------------------------------------------------------------------

export function setRowText(
	state: BuilderState,
	blockId: string,
	field: string,
	index: number,
	value: string
): BuilderState {
	return updateBlock(state, blockId, (b) => {
		switch (field) {
			// Summary row label / value
			case "rlbl": {
				const rows = [...((b.data.rows as SummaryRow[]) || [])];
				if (!rows[index]) return b;
				rows[index] = { ...rows[index], label: value };
				return { ...b, data: { ...b.data, rows } };
			}
			case "rval": {
				const rows = [...((b.data.rows as SummaryRow[]) || [])];
				if (!rows[index]) return b;
				rows[index] = { ...rows[index], value };
				return { ...b, data: { ...b.data, rows } };
			}
			// Raw section-header title (editable title portion without the "# " prefix)
			case "rraw": {
				const rows = [...((b.data.rows as SummaryRow[]) || [])];
				if (!rows[index]) return b;
				// Strip any leading "#" + spaces the user may have typed, then re-add the canonical "# " prefix.
				const stripped = value.replace(/^#+\s*/, "");
				rows[index] = { ...rows[index], raw: "# " + stripped };
				return { ...b, data: { ...b.data, rows } };
			}
			// Pair-column right-side label / value
			case "rlbl2": {
				const rows = [...((b.data.rows as SummaryRow[]) || [])];
				if (!rows[index]) return b;
				rows[index] = { ...rows[index], label2: value };
				return { ...b, data: { ...b.data, rows } };
			}
			case "rval2": {
				const rows = [...((b.data.rows as SummaryRow[]) || [])];
				if (!rows[index]) return b;
				rows[index] = { ...rows[index], value2: value };
				return { ...b, data: { ...b.data, rows } };
			}
			// Notes rows layout
			case "nk": {
				const rows = [...((b.data.rows as NoteRow[]) || [])];
				if (!rows[index]) return b;
				rows[index] = { ...rows[index], k: value };
				return { ...b, data: { ...b.data, rows } };
			}
			case "nv": {
				const rows = [...((b.data.rows as NoteRow[]) || [])];
				if (!rows[index]) return b;
				rows[index] = { ...rows[index], v: value };
				return { ...b, data: { ...b.data, rows } };
			}
			// Notes list items
			case "nli": {
				const items = [...(b.data.items || [])];
				if (index < 0 || index >= items.length) return b;
				items[index] = value;
				return { ...b, data: { ...b.data, items } };
			}
			// Footer lines
			case "fline": {
				const lines = [...(b.data.lines || [])];
				if (index < 0 || index >= lines.length) return b;
				lines[index] = value;
				return { ...b, data: { ...b.data, lines } };
			}
			// Header freeform lines
			case "hdrline": {
				const lines = [...(b.data.lines || [])];
				if (index < 0 || index >= lines.length) return b;
				lines[index] = value;
				return { ...b, data: { ...b.data, lines } };
			}
			// Direct block.data scalar fields (index unused)
			default:
				return { ...b, data: { ...b.data, [field]: value } };
		}
	});
}

// ---------------------------------------------------------------------------
// PresentationProfile + applyPresentation — presentation-only transform.
// Changes column layout, density and optional brand color without touching
// any text content (labels, values, tokens, header, notes, footer).
// ---------------------------------------------------------------------------

export interface PresentationProfile {
	summaryColumns: 1 | 2;
	density: "compact" | "comfortable" | "spacious";
	brand?: string; // hex; when set, also applied to theme.brand
}

/**
 * Normalize a summary rows array to all-single rows.
 * - Raw rows (row.raw !== undefined) pass through unchanged.
 * - Pair rows (row.label2 !== undefined) are split into two single rows.
 * - Single rows pass through unchanged.
 */
function toSingleRows(rows: SummaryRow[]): SummaryRow[] {
	const result: SummaryRow[] = [];
	for (const row of rows) {
		if (row.raw !== undefined) {
			result.push(row);
		} else if (row.label2 !== undefined) {
			result.push({ label: row.label, value: row.value, emphasis: row.emphasis });
			result.push({ label: row.label2, value: row.value2, emphasis: row.emphasis2 });
		} else {
			result.push(row);
		}
	}
	return result;
}

/**
 * Pair adjacent single rows into two-column pair rows.
 * Raw rows are never paired with anything — they are emitted solo and act as
 * barriers that prevent pairing across section boundaries.
 */
function toPairRows(rows: SummaryRow[]): SummaryRow[] {
	const singles = toSingleRows(rows);
	const result: SummaryRow[] = [];
	let i = 0;
	while (i < singles.length) {
		const cur = singles[i];
		// Raw rows pass through unpaired and reset the pairing cursor.
		if (cur.raw !== undefined) {
			result.push(cur);
			i++;
			continue;
		}
		// Look ahead for the next non-raw single row.
		const next = singles[i + 1];
		if (next !== undefined && next.raw === undefined) {
			result.push({
				label: cur.label,
				value: cur.value,
				emphasis: cur.emphasis,
				label2: next.label ?? "",
				value2: next.value ?? "",
				emphasis2: next.emphasis,
			});
			i += 2;
		} else {
			result.push(cur);
			i++;
		}
	}
	return result;
}

export function applyPresentation(state: BuilderState, profile: PresentationProfile): BuilderState {
	const blocks = state.blocks.map((b): Block => {
		// Reshape summary rows according to column mode.
		let updated = b;
		if (b.type === "summary") {
			const rows = (b.data.rows as SummaryRow[]) || [];
			const newRows = profile.summaryColumns === 2 ? toPairRows(rows) : toSingleRows(rows);
			updated = { ...b, data: { ...b.data, rows: newRows } };
		}
		// Apply density to every block (merge into existing style).
		updated = { ...updated, style: { ...updated.style, density: profile.density } };
		return updated;
	});

	const theme = profile.brand !== undefined ? { ...state.theme, brand: profile.brand } : state.theme;

	return { ...state, blocks, theme };
}

// ---------------------------------------------------------------------------
// Internal helper — immutably update one block by id
// ---------------------------------------------------------------------------

function updateBlock(state: BuilderState, blockId: string, fn: (b: Block) => Block): BuilderState {
	const blocks = state.blocks.map((b) => (b.id === blockId ? fn(b) : b));
	return { ...state, blocks };
}
